import {
  FilterScopeProvider,
  PITCH_POSITION_KEYS,
  ResetAllFiltersButton,
  TeamPageFilters,
} from '@statsbomb/kitbag-global-football';
import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { competitionOptionsAtom, defaultCompetitionSectionCycleAtom } from '@/atoms/filters/highLevel/competitions';
import { seasonOptionsAtom } from '@/atoms/filters/highLevel/seasons';
import { teamGamesBySeasonAtom } from '@/atoms/filters/team/teamGames';
import { unwrappedAvailableEventPeriodsAtom } from '@/atoms/queries/availableEventPeriods';
import { unwrappedTeamSquadPlayerOptionsAtom } from '@/atoms/queries/team/teamSquadData';
import { teamIdAtom } from '@/atoms/team/team';
import { usePhysicalMetricsFeature } from '@/hooks/featureFlags';
import { useComparisonFilterTabsActive } from '@/hooks/useComparisonFilterTabsActive';
import { toGameSelectorGroups } from '@/utils/games';

const unwrappedCompetitionOptionsAtom = unwrap(competitionOptionsAtom, prev => prev ?? []);
const unwrappedSeasonOptionsAtom = unwrap(seasonOptionsAtom, prev => prev ?? []);
const unwrappedCyclesAtom = unwrap(defaultCompetitionSectionCycleAtom, prev => prev ?? []);
const unwrappedGamesBySeasonAtom = unwrap(teamGamesBySeasonAtom, prev => prev ?? []);

const TeamHighLevelFiltersContent = ({ isTeamVisPage, teamId }: { isTeamVisPage: boolean; teamId: number }) => {
  const { t } = useTranslation(['filters', 'events', 'kitbagGlobalFootball']);
  const showInlineReset = useComparisonFilterTabsActive();
  const showTracking = usePhysicalMetricsFeature();
  const competitionOptions = useAtomValue(unwrappedCompetitionOptionsAtom);
  const seasonOptions = useAtomValue(unwrappedSeasonOptionsAtom);
  const cycles = useAtomValue(unwrappedCyclesAtom);
  const gamesBySeason = useAtomValue(unwrappedGamesBySeasonAtom);
  const availableMatchPeriods = useAtomValue(unwrappedAvailableEventPeriodsAtom);
  const squadPlayerOptions = useAtomValue(unwrappedTeamSquadPlayerOptionsAtom);
  const gamesGroupedBySeason = toGameSelectorGroups(gamesBySeason);

  const positionLabels = Object.fromEntries(
    PITCH_POSITION_KEYS.map(key => [key, t(`fields.event.attributes.playerPosition.${key}`, { ns: 'events' })]),
  );

  return (
    <TeamPageFilters
      teamId={teamId}
      competitionOptions={competitionOptions}
      seasonOptions={seasonOptions}
      playerCycles={cycles}
      gamesGroupedBySeason={gamesGroupedBySeason}
      positionLabels={positionLabels}
      positionDescription={t('positionHelperText', { ns: 'filters' })}
      showTabs={isTeamVisPage}
      hidePitch={!isTeamVisPage}
      hideAttributes={!isTeamVisPage}
      availableMatchPeriods={isTeamVisPage ? availableMatchPeriods : undefined}
      squadPlayerOptions={isTeamVisPage ? squadPlayerOptions : undefined}
      headerActions={showInlineReset ? <ResetAllFiltersButton /> : undefined}
      showTracking={showTracking}
    />
  );
};

export const TeamHighLevelFilters = () => {
  const teamId = useAtomValue(teamIdAtom);
  const isTeamVisPage = !!useMatch('/:locale/team/:teamId/vis/*');

  if (!teamId) return null;

  return (
    <FilterScopeProvider scope="main">
      <TeamHighLevelFiltersContent isTeamVisPage={isTeamVisPage} teamId={teamId} />
    </FilterScopeProvider>
  );
};
